/**
 * THE SHEET'S ONE FOOTER: which verbs a ticket offers right now, and the one
 * sentence that says what the primary does.
 *
 * Every state reads the same way: a sentence, one quiet secondary, one blue
 * primary on the right. Eight states cover the work a Team does:
 *
 *   proposed       Keep for a human · Start plan
 *   plan           Ask @yagni · Send back · Approve plan (dark while
 *                  questions are open, while Reeve writes an answer or a
 *                  note into the plan, when one could not be, and while Ask
 *                  changes wait)
 *   ask            Send back to Plan · Send answer and resume
 *   working        Stop · Watch live (gone once the sheet is wide)
 *   stopped_infra  Keep for a human · Run again (Review the plan again once its extra attempt is used)
 *   stopped        Keep for a human · Start over (a lane attempt that stopped and asked nothing)
 *   out_of_rounds  Keep for a human · One more round
 *   merge          Request changes · Merge where GitHub would merge it (or View pull request)
 *   done           View pull request · Next item
 *
 * Anything else that asks a person (a delivery to mark done, a held review to
 * confirm, an act the eight do not name) gets the generic footer, built from
 * the gate's own copy, so no open ask is ever stranded without a verb.
 *
 * A stopped build whose ask recommends one of its one-click ways on (Continue
 * anyway, the plan patch, the plan run again) puts that act on the blue
 * button, in the words the bell uses (`stopActLabel`); the stop's usual
 * primary moves to the More menu (`more`).
 *
 * The verbs are offered only where `@yagni/shared`'s predicates allow them
 * (`workItemVerbs.ts`), the same rules the item rail acts on. This module is
 * pure: the sheet executes the verb ids (`useSheetActs`).
 */
import type { HandOffAsk, JobAuthority, WorkItem, WorkItemGate } from "@yagni/shared";
import {
  canHandBack,
  canRunAgain,
  canStartOver,
  canStopRun,
  GATE_NOT_A_MEMBER,
  gatePrimaryBlock,
  gateStopKind,
  isClosedPullRequestStop,
  isHeldBeforeBuild,
  isLiveRun,
  isPausedRunAsk,
  isPlanAsk,
  isRepoChoiceAsk,
  JOB_ACT_COPY,
  jobActCappedCopy,
  pendingPlanAmendmentIds,
  jobActElsewhere,
  jobActVariantCopy,
  PR_CLOSED_UNMERGED_STOP_REASON,
  readGateAsk,
  readHandOffAsk,
  readJobGatePayload,
  recommendedStopAct,
  resumesApprovedPlan,
  stopActLabel,
  type RecommendedStopAct,
  type RunAgainFacts,
} from "@yagni/shared";
import type { MergeFacts } from "../work/MergeFooter.js";
import {
  ADD_NOTE_LABEL,
  viewPrLabel,
  approvalDismissedWhy,
  canFixConflicts,
  fixConflictsWhy,
  FIX_CONFLICTS_VERB,
  gitHubAllowsMerge,
  gitHubStateLine,
  mergingBaseWhy,
  prConflictLine,
  settleSummaryLine,
  DISCARDED_FOOTER,
  OPEN_NEW_PLAN,
  PLAN_WITHOUT_CHECK_VERB,
  SCOPE_NOW_VERB,
  DAILY_READINGS_SETTINGS,
  TRACKER_SPEC_DAILY_CAP_ASK_OWNER,
  TRACKER_SPEC_DAILY_CAP_WHY,
  TRACKER_SPEC_QUEUED_BOLD,
  TRACKER_SPEC_QUEUED_WHY,
  trackerSpecDailyCapBold,
  trackerSpecReadingWhy,
  trackerSpecWaitOf,
  type TrackerSpecWait,
  planSignOffBold,
  START_OVER_MENU,
  startPlanWhy,
} from "../work/workCopy.js";
import { itemStoppedSentence, pipelineStageOf, STOPPED_RUN_STATUSES } from "../work/workStage.js";

/** What a repository ask's footer says: the picker above is the answer. */
export const REPO_ASK_WHY = "Pick the repository above. Planning starts as soon as you pick one.";

export type FooterKind =
  | "proposed"
  | "plan"
  | "ask"
  | "choose_repo"
  | "working"
  | "stopped_infra"
  | "stopped"
  | "out_of_rounds"
  | "merge"
  | "done"
  | "generic"
  | "bespoke"
  | "not_member"
  | "loading";

/** Every verb a footer can carry. The sheet's executor maps each to its route. */
export type SheetVerbId =
  | "start_plan"
  | "scope_now"
  | "keep_proposal"
  | "approve_plan"
  | "send_back"
  | "ask_about_plan"
  | "send_answer"
  | "send_back_to_plan"
  | "dismiss_ask"
  | "keep_for_human"
  | "stop_run"
  | "add_note"
  | "hand_back"
  | "watch_live"
  | "run_again"
  | "run_again_gate"
  | "continue_anyway"
  | "patch_plan"
  | "reoffer_plan"
  | "one_more_round"
  | "stop_round"
  | "act"
  | "decline_act"
  | "request_changes"
  | "fix_conflicts"
  | "merge"
  | "open_pr"
  | "confirm_review"
  | "confirm_held_review"
  | "mark_done"
  | "request_result_changes"
  | "commission"
  | "push_milestone"
  | "start_planning"
  | "next_item"
  | "open_successor"
  | "start_over"
  | "retry_gate"
  | "ask_yagni"
  | "ask_to_join"
  | "take_over"
  | "open_link";

export interface FooterVerb {
  id: SheetVerbId;
  label: string;
  disabled?: boolean;
}

export interface SheetFooterSpec {
  kind: FooterKind;
  /** The generic footer's label: what is being asked, in the gate's own words. */
  label?: string;
  /** What the primary does and what it touches. */
  why: string;
  /** The part of `why` that matters, printed bold after it. */
  bold?: string;
  secondary: FooterVerb | null;
  primary: FooterVerb | null;
  /**
   * A quiet verb before the secondary that only collects words: the plan
   * gate's "Ask @yagni" opens the Ask dock, so a question is never a
   * send-back (ENG-2966); a live run's "Steer" opens the note's dialog,
   * read at the next build step or fix round (YAG-1116).
   */
  ask?: FooterVerb;
  /**
   * The sentence says what the person has to do before a verb works (an
   * added source to check, the questions still open, a gate that could not
   * be read), so it stays printed instead of behind the "i" (`whyStaysInline`).
   */
  whyInline?: boolean;
  /** Verbs the ask's recommended act displaced from the primary: the More menu draws them. */
  more?: FooterVerb[];
  /**
   * `not_member` only: what the Team's members can do here, said lower case
   * after their names ("Maya and Jack can start the plan").
   */
  memberVerb?: string;
  /**
   * An optional note the primary carries (a stopped round's retry): the
   * sheet shows its box, with this placeholder, before anything is pressed.
   */
  note?: { placeholder: string };
  /**
   * An in-app link said after `why`, as "<lead> <label>." (a spent daily cap's
   * "Raise the limit in Settings → Workspace."). The sheet renders it as a link.
   */
  link?: { lead: string; label: string; href: string };
}

/** What a member can do at an act, in a sentence: "start the plan", "approve the plan". */
const MEMBER_VERB: Partial<Record<string, string>> = {
  accept_proposal: "start the plan",
  proceed_to_build: "approve the plan",
};

function memberVerbOf(act: string, verbLabel: string): string {
  const named = MEMBER_VERB[act];
  if (named) return named;
  const label = verbLabel.trim();
  return label ? `${label[0]!.toLowerCase()}${label.slice(1)}` : "answer this";
}

export interface SheetFooterFacts {
  item: WorkItem;
  gate: WorkItemGate | null;
  gateLoading: boolean;
  /** The open-gate read failed: what the ticket waits on is unknown, not nothing. */
  gateError?: boolean;
  /** The item's runs, newest first. `lane` marks a one-attempt lane attempt's run. */
  runs: ReadonlyArray<RunAgainFacts & { id: string }>;
  /**
   * The plan's questions: how many asked, how many answered (saved on the
   * server, or on record), and what was said at the gate that the plan does
   * not hold yet (spec 2026-11-02): inputs Reeve is still writing in
   * (`applying`) and ones that could not be (`failed`). The Ask changes
   * waiting for Accept or Reject are read off the gate.
   */
  questions: {
    answered: number;
    total: number;
    applying?: number;
    failed?: number;
  };
  /**
   * How many of the open questions are facts. A fact has no recommendation,
   * so skipping one does not let Reeve choose; the sentence must not say so.
   */
  openFacts?: number;
  /**
   * How many of the open questions Skip cannot settle: facts, one-way
   * decisions and questions with no recommendation. Only a person answers
   * those, so "Skipping lets Reeve choose" must not be said of them.
   */
  openNoSkip?: number;
  /** The decisions Reeve states on the plan that approving accepts (none changed by the reader). */
  reeveDecisions?: number;
  /** The facts Reeve states on the plan (checked or assumed) that approving accepts. */
  reeveFacts?: number;
  /** Connected tools added to the Team from the question cards (their names), this visit. */
  addedSources?: readonly string[];
  /** A written answer is ready to send on a needs_input ask. */
  answerReady: boolean;
  /** The ticket read's merge facts; null while they load or when the ticket is not in Review. */
  merge: MergeFacts | null;
  /** The sheet is the full page already. */
  wide: boolean;
  /** Another ticket on the Team needs the reader. */
  hasNextItem: boolean;
  /** A person may start (or retry) the plan from here (`canStartItemPlan`). */
  canStartPlan: boolean;
  /** Is the reader on the ticket's Team? Undefined until the roster says. */
  viewerIsTeamMember?: boolean;
  /** The worker the ticket is with, for the sentences. */
  workerName?: string | null;
  /**
   * The rung of the Team's Plan line: on an Autonomous line Reeve approves
   * his own plan, so no sentence promises a sign-off. Null or absent while
   * unknown, which keeps the sign-off words.
   */
  planAuthority?: JobAuthority | null;
  /**
   * The item's runs are still being read. Until they land, a ticket that names
   * a run reads as loading, never as "Waiting its turn".
   */
  runsLoading?: boolean;
  /**
   * The reader may change workspace settings (the owner, or a superadmin),
   * the same gate `PATCH /api/workspaces/:id/settings` holds.
   */
  canEditWorkspaceSettings?: boolean;
}

/** Agent decisions resolve through the Agent's own record, never the gate route. */
const WORKER_DECISION_GATE_KINDS: ReadonlySet<string> = new Set([
  "approve_worker_instruction",
  "publish_worker_review",
]);

const MILESTONE_GATE_KIND = "milestone_decomposition";

function verb(id: SheetVerbId, label: string, disabled = false): FooterVerb {
  return disabled ? { id, label, disabled } : { id, label };
}

/** The plan gate's question verb: opens the plan-review dock with a question, never a send-back. */
const ASK_ABOUT_PLAN: FooterVerb = { id: "ask_about_plan", label: "Ask @yagni" };

/**
 * Whether the footer's sentence stays printed. It goes behind the "i" when it
 * only says what the primary does; it stays when it is why the primary cannot
 * be pressed, when there is no primary (the sentence is all the footer says),
 * or when the spec says the person needs it to act.
 */
export function whyStaysInline(spec: Pick<SheetFooterSpec, "primary" | "whyInline">): boolean {
  return spec.whyInline === true || !spec.primary || spec.primary.disabled === true;
}

function isTerminal(item: WorkItem): boolean {
  return item.phase === "done" || item.phase === "dismissed" || item.phase === "split";
}

function heldReviewGate(gate: WorkItemGate): boolean {
  const payload = gate.payload as Record<string, unknown> | null;
  return Boolean(payload && payload.heldReviewPayload);
}

/**
 * The open-questions sentence. Skip takes a question's recommendation only
 * where Skip may settle it, so "Skipping lets Reeve choose" is said of those
 * alone. A fact has no recommendation (Reeve could not check it), and a
 * one-way decision or a question with no recommendation is a person's to
 * answer. `openNoSkip` counts all three and includes the facts.
 */
function openQuestionsWhy(open: number, openFacts: number, openNoSkip = openFacts): string {
  const ask = `Answer the ${open} open question${open === 1 ? "" : "s"} first.`;
  const facts = Math.min(Math.max(0, openFacts), open);
  const noSkip = Math.min(Math.max(facts, openNoSkip), open);
  if (noSkip === 0) return `${ask} Skipping lets Reeve choose.`;
  if (facts === open)
    return `${ask} Reeve could not check ${open === 1 ? "this fact" : "these facts"}.`;
  if (noSkip === open) return `${ask} Only a person can settle ${open === 1 ? "it" : "them"}.`;
  if (facts === noSkip)
    return `${ask} Reeve could not check ${facts} of them; skipping the others lets Reeve choose.`;
  return `${ask} Skipping lets Reeve choose on ${open - noSkip} of them; the rest need a person.`;
}

/** What Approve does, naming what of Reeve's it accepts when there is any. */
function approvesWhy(reeveDecisions: number, reeveFacts = 0): string {
  const parts = [
    ...(reeveDecisions > 0
      ? [reeveDecisions === 1 ? "decision" : `${reeveDecisions} decisions`]
      : []),
    ...(reeveFacts > 0 ? [reeveFacts === 1 ? "1 fact" : `${reeveFacts} facts`] : []),
  ];
  if (parts.length === 0)
    return "Approving opens a sandbox and starts the build. You can stop it at any time.";
  return `Approving accepts Reeve's ${parts.join(" and ")}, opens a sandbox and starts the build.`;
}

/** Why a plan's Approve is dark, in words. */
function planBlockWhy(
  block: ReturnType<typeof gatePrimaryBlock>,
  open: number,
  act: string | null,
  openFacts = 0,
  openNoSkip = openFacts,
): string | null {
  switch (block) {
    case "questions_open":
      return openQuestionsWhy(open, openFacts, openNoSkip);
    case "plan_no_files":
      return "This plan names no file to change, so there is nothing to build yet. Send it back with a note on where the change lives.";
    case "plan_placeholder_files":
      return "This plan's file list still holds placeholders, so it cannot be built as written. Send it back so the files are named.";
    case "act_elsewhere":
      return jobActElsewhere(act as never);
    case "plan_edit_in_progress":
      return PLAN_EDIT_IN_PROGRESS_WHY;
    case "plan_answer_failed":
      return PLAN_ANSWER_FAILED_WHY;
    case "plan_changes_pending":
      return PLAN_CHANGES_PENDING_WHY;
    default:
      return null;
  }
}

/** Approve waits while Reeve writes an answer or a note into the plan. */
export const PLAN_EDIT_IN_PROGRESS_WHY =
  "Reeve is updating the plan for your answer. Approve once it lands.";
/** Approve waits on an answer or a note the plan could not take. */
export const PLAN_ANSWER_FAILED_WHY =
  "An answer could not be applied to the plan. Retry it, keep it as a decision, or start over.";
/** Approve waits on changes proposed in Ask. */
export const PLAN_CHANGES_PENDING_WHY = "Accept or reject the proposed changes first.";

function doneFooter(facts: SheetFooterFacts): SheetFooterSpec {
  const { item } = facts;
  // Thrown away with Start over: the work carries on in the attempt that replaced it.
  if (item.phase === "dismissed" && item.discard?.successorWorkItemId) {
    return {
      kind: "done",
      why: DISCARDED_FOOTER,
      secondary: item.prUrl ? verb("open_pr", viewPrLabel(item.prNumber)) : null,
      primary: verb("open_successor", OPEN_NEW_PLAN),
    };
  }
  const why: Record<string, string> = {
    dismissed: "Closed with no work done. The ticket is still in your tracker for a person.",
    split: "Split into its own tickets. Each one carries on in the queue.",
  };
  return {
    kind: "done",
    why: why[item.phase] ?? "Finished work is the record. Nothing here can be changed.",
    secondary: item.prUrl ? verb("open_pr", viewPrLabel(item.prNumber)) : null,
    primary: facts.hasNextItem
      ? verb("next_item", "Next item")
      : verb("next_item", "Back to the queue"),
  };
}

/**
 * The one-attempt lane's hand-off asks that need no words: the setup ask,
 * whose answer is running the repository's setup; an install that failed,
 * a person's, whose answer is running again once its setup or a variable is
 * changed; and a stop on our side, whose answer is running again. Null for
 * every other gate, any other person's stop on the lane included: that one
 * asks a question.
 */
export function laneAskOf(
  gate: WorkItemGate,
): { kind: "run_setup" | "install_failed" | "run_again"; ask: HandOffAsk } | null {
  const ask = readHandOffAsk(gate.payload);
  if (!ask) return null;
  if (ask.askKind === "run_setup") return { kind: "run_setup", ask };
  if (gate.payload?.stopReason === "env_install_failed") return { kind: "install_failed", ask };
  return gate.payload?.stopClass === "infra" ? { kind: "run_again", ask } : null;
}

/** Wright's last ask on a pull request: every review point handled, and a person approves or merges it on GitHub. */
export function isReadyForPerson(gate: WorkItemGate): boolean {
  const job = readJobGatePayload(gate.payload);
  return (
    job?.act === "push_fix" &&
    job.evidence.kind === "pr_needs_person" &&
    job.evidence.askKind === "ready_for_person"
  );
}

/** What Keep for a human does, said wherever a stopped footer offers it. */
/** The one label Hand back reads, wherever a held ticket's footer offers it. */
const HAND_BACK = "Hand back to Wright";
/** Hand back on a held ticket that never built: Reeve plans it next. */
const HAND_TO_REEVE = "Hand to Reeve";

/** A person holds the ticket: they took a run over, and it is theirs until they hand it back. */
function heldByAPerson(item: WorkItem): boolean {
  return item.assigneeType === "human";
}

const QUEUE_RESUMES = "The Team's queue starts it on its next run.";

/** The plan-start refusals that resume on their own: the label, and when it resumes. */
const SELF_HEALING_PLAN_REFUSALS: Readonly<Record<string, { label: string; resumes: string }>> = {
  at_concurrency: { label: "Waiting its turn", resumes: "It starts on its own when a slot frees." },
  no_capacity: {
    label: "Waiting on credits",
    resumes: "It starts on its own once credits are added.",
  },
  rollout_stopped: {
    label: "Paused by an operator",
    resumes: "It starts on its own when the pause lifts.",
  },
};

/**
 * Planning was refused and nothing has started since: say why, in the
 * refusal's own sentence, with the one way forward. A capacity hold or an
 * operator's pause resumes on its own (the retry lane, or the build queue's
 * next firing) and says when; every other refusal is a person's to clear, so the
 * footer never reads "Waiting its turn" over one.
 */
function standingRefusalFooter(facts: SheetFooterFacts): SheetFooterSpec | null {
  const refusal = facts.item.planStartRefusal;
  if (!refusal) return null;
  const healing = SELF_HEALING_PLAN_REFUSALS[refusal.code];
  if (healing) {
    return {
      kind: "generic",
      label: healing.label,
      // The build queue's own hold resumes on its next run, not the moment the room frees.
      why: `${refusal.reason} ${refusal.via === "work_queue" ? QUEUE_RESUMES : healing.resumes}`,
      secondary: null,
      primary: facts.hasNextItem ? verb("next_item", "Next item") : verb("ask_yagni", "Ask YAGNI"),
    };
  }
  return {
    kind: "generic",
    label: "Plan did not start",
    why: facts.canStartPlan
      ? `${refusal.reason} Nothing retries it on its own.`
      : `${refusal.reason} Nothing here retries it on its own. YAGNI can say what it needs.`,
    secondary: verb("keep_for_human", "Keep for a human"),
    primary: facts.canStartPlan
      ? verb("start_planning", "Retry plan")
      : verb("ask_yagni", "Ask YAGNI"),
  };
}

const KEEP_FOR_HUMAN_WHY =
  "Keep for a human closes it in YAGNI; the tracker ticket stays with your team.";

/** A stop that keeps the approved plan: Run again builds it at once. */
const RUN_AGAIN_WHY = "Running again builds the same plan now.";

/** The approval already spent its one extra attempt, so Run again brings the plan back for approval. */
function rerunNeedsApproval(gate: WorkItemGate): boolean {
  return gate.payload?.rerunNeedsApproval === true;
}

function rerunWhy(gate: WorkItemGate): string | null {
  return rerunNeedsApproval(gate)
    ? "This plan already had its extra attempt, so running again brings the plan back for your approval."
    : null;
}

function runAgainGateVerb(gate: WorkItemGate, disabled = false): FooterVerb {
  return verb(
    "run_again_gate",
    rerunNeedsApproval(gate) ? "Review the plan again" : "Run again",
    disabled,
  );
}

const STOP_ACT_VERB: Record<RecommendedStopAct, SheetVerbId> = {
  continue_anyway: "continue_anyway",
  patch_plan: "patch_plan",
  reoffer: "reoffer_plan",
};

/** What the recommended act does, on a stop whose usual sentence is about the answer. */
function askActWhy(
  act: RecommendedStopAct,
  gate: WorkItemGate,
): Pick<SheetFooterSpec, "why" | "bold"> {
  const patch = readGateAsk(gate.payload)?.patch;
  if (act === "patch_plan" && patch?.kind === "use_checks") {
    return {
      why: "Using them changes the plan's checks and resumes the build.",
      bold: "Nothing reaches main until a person merges.",
    };
  }
  if (act === "patch_plan") {
    const n = patch?.values.length ?? 0;
    return {
      why:
        n === 1
          ? "Adding it puts the file in the approved plan and resumes the build."
          : `Adding them puts the ${n} files in the approved plan and resumes the build.`,
      bold: "Nothing reaches main until a person merges.",
    };
  }
  if (act === "continue_anyway") {
    return { why: "Continuing gives the approved plan one more attempt.", bold: undefined };
  }
  return { why: "Running the plan again brings it back for your approval.", bold: undefined };
}

/**
 * The ask's recommended act on the blue button, in the bell's words, when the
 * shared rules offer it here; the stop's usual primary moves to More, as it
 * was. On a stop that keeps the approved plan, running the plan again is Run
 * again itself, so it takes the button and nothing moves.
 */
function withRecommendedAct(
  spec: SheetFooterSpec,
  gate: WorkItemGate,
  item: WorkItem,
  infraStop: boolean,
): SheetFooterSpec {
  const act = recommendedStopAct(gate);
  const label = act ? stopActLabel(readGateAsk(gate.payload), { infraStop }) : null;
  if (!act || !label || !spec.primary) return spec;
  // The extra attempt is spent: the plan comes back for approval, and the button says so.
  if (act === "reoffer" && infraStop && rerunNeedsApproval(gate)) return spec;
  const primary = verb(STOP_ACT_VERB[act], label, Boolean(item.removedRepoRef));
  if (act === "reoffer" && infraStop) return { ...spec, primary };
  return { ...spec, ...recommendedWhy(spec, act, gate, infraStop), primary, more: [spec.primary] };
}

/**
 * The sentence beside the recommended act. On a stop that keeps the approved
 * plan, Continue anyway builds it now like Run again, so the stop's own
 * sentence holds; anything else says what the act does.
 */
function recommendedWhy(
  spec: SheetFooterSpec,
  act: RecommendedStopAct,
  gate: WorkItemGate,
  infraStop: boolean,
): Pick<SheetFooterSpec, "why" | "bold"> | null {
  if (infraStop && act !== "patch_plan") return null;
  const said = askActWhy(act, gate);
  return spec.secondary?.id === "keep_for_human"
    ? { ...said, why: `${said.why} ${KEEP_FOR_HUMAN_WHY}` }
    : said;
}

/**
 * A stopped round's blue button follows its cause, as the round's ask names
 * it: the page that fixes the cause, or Take it over once the round stopped
 * the same way twice. Try the round again then waits under More, and the note
 * box goes with it: the blue button no longer carries one.
 */
function stoppedRoundFooter(
  spec: SheetFooterSpec,
  gate: WorkItemGate,
  variant: ReturnType<typeof jobActVariantCopy>,
): SheetFooterSpec {
  const ask = readGateAsk(gate.payload);
  if (!ask || !spec.primary) return spec;
  const { note: _note, ...rest } = spec;
  if (ask.link) {
    // The command number stays on the ask's link; the button fits a narrow
    // sheet. Ask copy separates its parts with " · " (`stopLink`, workCopy.ts).
    const label = ask.link.label.split(" · ")[0] ?? ask.link.label;
    return { ...rest, why: ask.need, primary: verb("open_link", label), more: [spec.primary] };
  }
  if (ask.recommended?.action === "take_over" && variant?.alternative) {
    return {
      ...rest,
      why: variant.alternative.effect,
      primary: verb("take_over", variant.alternative.label),
      more: [spec.primary],
    };
  }
  return spec;
}

function gateFooter(facts: SheetFooterFacts, gate: WorkItemGate): SheetFooterSpec {
  const { item } = facts;
  if (WORKER_DECISION_GATE_KINDS.has(gate.kind)) {
    return {
      kind: "bespoke",
      why: "Decide it on the Agent's record above.",
      secondary: null,
      primary: null,
    };
  }

  // A run that stopped and asked.
  if (isPausedRunAsk(gate)) {
    if (gateStopKind(gate) === "infra_stop") {
      return withRecommendedAct(
        {
          kind: "stopped_infra",
          why: `${rerunWhy(gate) ?? RUN_AGAIN_WHY} ${KEEP_FOR_HUMAN_WHY}`,
          bold: "The kept branch is reused.",
          secondary: verb("keep_for_human", "Keep for a human"),
          primary: runAgainGateVerb(gate, Boolean(item.removedRepoRef)),
        },
        gate,
        item,
        true,
      );
    }
    return withRecommendedAct(
      {
        kind: "ask",
        why: facts.answerReady
          ? "Sending your answer resumes the work where it paused."
          : "Answer the question first, or send it back to Plan with a note.",
        bold: facts.answerReady ? "Nothing is lost." : undefined,
        secondary: verb("send_back_to_plan", "Send back to Plan"),
        primary: verb(
          "send_answer",
          "Send answer and resume",
          !facts.answerReady || Boolean(item.removedRepoRef),
        ),
      },
      gate,
      item,
      false,
    );
  }

  if (isRepoChoiceAsk(gate)) {
    // The Team accepted this on its own and could not tell which repository:
    // the picker in the body is the answer, and picking starts planning.
    return {
      kind: "choose_repo",
      why: REPO_ASK_WHY,
      secondary: verb("dismiss_ask", "Keep for a human"),
      primary: verb("start_planning", "Start plan", true),
    };
  }

  if (gate.kind === "needs_input") {
    const lane = laneAskOf(gate);
    if (lane?.kind === "run_setup") {
      // The answer is the run: the server starts the repository's setup and
      // then resumes the attempt (`attempts/handOff.ts`), whoever answers.
      return {
        kind: "ask",
        // A setup whose last run asked a person would ask again as it is.
        why:
          lane.ask.setupState === "needs_you"
            ? "The setup needs a change first: make it on the repository's page, then Run setup."
            : "Run setup starts the repository's setup; the attempt goes on once its environment is ready.",
        secondary: verb("dismiss_ask", "Keep for a human"),
        primary: verb("send_answer", "Run setup"),
      };
    }
    if (lane?.kind === "install_failed") {
      return {
        kind: "ask",
        why: "Change the repository's setup or Workspace variables first if they caused it. Running again installs with what is set now.",
        secondary: verb("dismiss_ask", "Keep for a human"),
        primary: verb("send_answer", "Run again"),
      };
    }
    if (lane?.kind === "run_again") {
      return {
        kind: "ask",
        why: "Running again resumes the attempt where it stopped. A note is optional.",
        secondary: verb("dismiss_ask", "Keep for a human"),
        primary: verb("send_answer", "Run again"),
      };
    }
    if (resumesApprovedPlan(gate)) {
      const closedPr = isClosedPullRequestStop(gate);
      const why =
        rerunWhy(gate) ??
        (closedPr
          ? "The pull request closed without a merge. Running again builds a new one from the approved plan."
          : "The approved plan is unchanged. Running again builds it now.");
      return withRecommendedAct(
        {
          kind: "stopped_infra",
          why: closedPr ? why : `${why} ${KEEP_FOR_HUMAN_WHY}`,
          secondary: closedPr
            ? verb("dismiss_ask", "Keep for a human")
            : verb("keep_for_human", "Keep for a human"),
          primary: runAgainGateVerb(gate),
        },
        gate,
        item,
        true,
      );
    }
    return {
      kind: "ask",
      why: facts.answerReady
        ? "Your answer goes on the ticket's record, and the work moves on from the question."
        : "Answer the question first, or keep it for a human.",
      secondary: verb("dismiss_ask", "Keep for a human"),
      primary: verb("send_answer", "Send answer and resume", !facts.answerReady),
    };
  }

  const job = readJobGatePayload(gate.payload);
  const act = job?.act ?? null;
  if (act && facts.viewerIsTeamMember === false) {
    // The sheet says it with the Team's people (`NotMemberWhy`); `why` is the
    // plain sentence for anywhere that cannot.
    const label =
      act === "publish_verdict" ? "Confirm review" : (JOB_ACT_COPY[act]?.verb ?? "Go ahead");
    return {
      kind: "not_member",
      why: GATE_NOT_A_MEMBER,
      memberVerb: memberVerbOf(act, label),
      secondary: verb("ask_to_join", "Ask to be added"),
      primary: verb("act", label, true),
    };
  }

  // The Ask changes waiting are the gate's own, read off it as the chat's verbs read them.
  const block = gatePrimaryBlock(
    gate,
    isPlanAsk(gate)
      ? { ...facts.questions, pendingChanges: pendingPlanAmendmentIds(gate).length }
      : { answered: 0, total: 0 },
  );
  const blocked = block != null;
  const open = Math.max(0, facts.questions.total - facts.questions.answered);

  if (act === "push_fix" && job?.cappedByPerItemCap) {
    const capped = jobActCappedCopy(act, job.job?.agentName);
    return {
      kind: "out_of_rounds",
      why: "One more round lets the builder answer the open threads and the reviewer look again.",
      bold: "The pull request stays open.",
      secondary: verb("stop_round", "Keep for a human"),
      primary: verb("one_more_round", "One more round", blocked || !capped),
    };
  }

  // A proposal and a plan resolve through their own lifecycle routes
  // (`accept_proposal`, `approve_execution_plan`), whether or not a Job minted
  // the gate with its act: `accept_proposal` and `proceed_to_build` have no
  // dispatcher, so `perform_job_act` answers them 409 "cannot be answered from
  // here yet" (staging, NMT-86 and NMT-90, 2026-10-11).
  if (gate.kind === "accept_proposal" || act === "accept_proposal") {
    return {
      kind: "proposed",
      why: "Starting the plan hands this to Reeve.",
      bold: planSignOffBold(facts.planAuthority),
      secondary: verb("keep_proposal", "Keep for a human"),
      primary: verb("start_plan", "Start plan", blocked),
    };
  }

  if (isPlanAsk(gate)) {
    const planId =
      gate.planId ?? (typeof gate.payload?.planId === "string" ? gate.payload.planId : null);
    const added = facts.addedSources ?? [];
    // A connected tool just added from a fact card: the way to use it is a
    // new plan, which Send back asks for. Said before anything else, so the
    // reader knows the Add did something and what to do next.
    const addedWhy = added.length
      ? `${added.join(" and ")} ${added.length === 1 ? "was" : "were"} added to this Team. Send back to let Reeve check ${added.length === 1 ? "it" : "them"} on the next plan${
          block === "questions_open"
            ? `, or answer the ${open} open question${open === 1 ? "" : "s"} to approve.`
            : "."
        }`
      : null;
    const darkWhy =
      addedWhy ??
      planBlockWhy(block, open, act, facts.openFacts, facts.openNoSkip ?? facts.openFacts) ??
      (!planId
        ? "This plan has no revision to approve yet. Send it back to have one written."
        : null);
    return {
      kind: "plan",
      why: darkWhy ?? approvesWhy(facts.reeveDecisions ?? 0, facts.reeveFacts ?? 0),
      bold: darkWhy ? undefined : "Nothing reaches main until a person merges.",
      ...(darkWhy ? { whyInline: true } : {}),
      ask: ASK_ABOUT_PLAN,
      secondary: verb("send_back", "Send back"),
      primary: verb("approve_plan", "Approve plan", blocked || !planId),
    };
  }

  if (gate.kind === "review_result") {
    if (heldReviewGate(gate)) {
      return {
        kind: "generic",
        label: "Confirm review",
        why: "Confirming posts what you kept and edited to the pull request as one review.",
        secondary: null,
        primary: verb("confirm_review", "Confirm review"),
      };
    }
    return {
      kind: "generic",
      label: "Delivery",
      why: "Marking it done puts this delivery on the record. Request changes sends your notes back for another pass.",
      secondary: verb("request_result_changes", "Request changes"),
      primary: verb("mark_done", "Mark done"),
    };
  }

  // Wright handled every review point and the pull request waits on a person
  // at GitHub: approving it there (or merging it) is the step, so the primary
  // opens the pull request. The ask closes by itself when GitHub moves.
  if (job && isReadyForPerson(gate)) {
    const decision = job.evidence.reviewDecision;
    const needsApproval = decision === "REVIEW_REQUIRED" || decision === "CHANGES_REQUESTED";
    const detail =
      typeof job.evidence.detail === "string" && job.evidence.detail.trim()
        ? job.evidence.detail.trim()
        : null;
    const label = gate.title || (needsApproval ? "Approve on GitHub" : "Merge");
    // main moved under the pull request: who fixes the conflict comes first.
    const conflictSpec = conflictFooter(facts, label);
    if (conflictSpec) return conflictSpec;
    if (facts.merge?.mainMerged?.approvalDismissed) {
      return {
        kind: "generic",
        label,
        why: approvalDismissedWhy(facts.merge.mainMerged),
        secondary: null,
        primary: item.prUrl ? verb("open_pr", viewPrLabel(item.prNumber)) : null,
      };
    }
    return {
      kind: "generic",
      label,
      why:
        detail ??
        (needsApproval
          ? "Wright handled every review point. GitHub needs a person's approval."
          : "Wright handled every review point. Merge it on GitHub; it reaches Done when the merge lands."),
      secondary: null,
      primary: item.prUrl ? verb("open_pr", viewPrLabel(item.prNumber)) : null,
    };
  }

  if (act) {
    // An act the eight do not name: its own copy, a fix round's variant first.
    const variant =
      act === "push_fix"
        ? jobActVariantCopy(act, job?.evidence.kind, job?.job?.agentName, job?.evidence.askKind)
        : null;
    const copy = JOB_ACT_COPY[act];
    // Wright's ask about its pull request, which also conflicts: Wright does
    // not push over the ask, so the footer says the answer merges it too.
    const alsoConflicts =
      act === "push_fix" && facts.merge?.conflict?.fixer === "ask_open"
        ? ` It also conflicts with ${facts.merge.conflict.base ?? "the base branch"}.`
        : "";
    const spec: SheetFooterSpec = {
      kind: "generic",
      label: gate.title || "Needs you",
      why:
        (planBlockWhy(block, open, act) ??
          variant?.effect ??
          copy?.effect ??
          "This needs your call before the work goes on.") + alsoConflicts,
      secondary: verb("decline_act", variant?.declineLabel ?? copy?.declineLabel ?? "Not now"),
      // "Publish the verdict" is retired as a label; the act is the review's confirm.
      primary: verb(
        "act",
        act === "publish_verdict" ? "Confirm review" : (variant?.verb ?? copy?.verb ?? "Go ahead"),
        blocked,
      ),
      ...(variant?.retryNotePlaceholder
        ? { note: { placeholder: variant.retryNotePlaceholder } }
        : {}),
    };
    return job?.evidence.kind === "round_stopped" ? stoppedRoundFooter(spec, gate, variant) : spec;
  }

  if (gate.kind === "commission_project") {
    return {
      kind: "generic",
      label: gate.title || "Commission the project",
      why: "Commissioning puts the project's tickets on the Team.",
      secondary: null,
      primary: verb("commission", "Commission"),
    };
  }
  if (gate.kind === MILESTONE_GATE_KIND) {
    return {
      kind: "generic",
      label: gate.title || "Push the milestone",
      why: "Pushing sends these tickets to the tracker under this milestone.",
      secondary: null,
      primary: verb("push_milestone", "Push to the tracker"),
    };
  }

  // A gate this build has no verbs for: ask, rather than leave it stranded.
  return {
    kind: "generic",
    label: gate.title || "Needs you",
    why: "This ask is answered somewhere this page cannot reach. YAGNI can say what it needs.",
    secondary: null,
    primary: verb("ask_yagni", "Ask YAGNI"),
  };
}

function mergeFooter(facts: SheetFooterFacts, merge: MergeFacts): SheetFooterSpec {
  if (merge.heldReview) {
    return {
      kind: "generic",
      label: "Confirm review",
      why: "Proctor approved this push. Confirm to post the approval and take the pull request out of draft.",
      secondary: null,
      primary: verb("confirm_held_review", "Confirm review"),
    };
  }
  if (merge.pullRequestDraft === true || merge.heldReviewElsewhere) {
    return {
      kind: "generic",
      label: "Draft",
      why: draftWhy(merge),
      secondary: null,
      primary: merge.pullRequestUrl ? verb("open_pr", viewPrLabel(facts.item.prNumber)) : null,
    };
  }
  const conflictSpec = conflictFooter(facts);
  if (conflictSpec) return conflictSpec;
  // Out of draft is not an approval: Wright takes a pull request out of draft
  // when nothing is left for it to fix, and Proctor never approves a pull
  // request YAGNI's bot wrote. The sentence is GitHub's state, and Merge is
  // offered only where GitHub would merge it now.
  const allowsMerge = gitHubAllowsMerge(merge.github);
  const inApp = merge.canMergeInApp && allowsMerge;
  const why = [
    settleSummaryLine(merge.settleSummary),
    gitHubStateLine(merge.github),
    allowsMerge && !inApp ? "Merge it on GitHub; it reaches Done when the merge lands." : null,
  ]
    .filter(Boolean)
    .join(" ");
  const mergeVerb = inApp
    ? verb("merge", "Merge")
    : merge.pullRequestUrl
      ? verb("open_pr", viewPrLabel(facts.item.prNumber))
      : null;
  // A person holds the pull request (they took the round over): handing it
  // back is the act; Merge stays beside it, and the round they would ask for
  // rides the note, so Request changes moves to More.
  if (heldByAPerson(facts.item) && facts.item.phase === "final_review") {
    return {
      kind: "merge",
      why: "You hold this pull request. Handing it back gives it to the Team;",
      bold: "Wright starts a round from your note for the next step, if you left one.",
      secondary: mergeVerb,
      primary: verb("hand_back", HAND_BACK),
      more: [verb("request_changes", "Request changes")],
    };
  }
  return {
    kind: "merge",
    why,
    bold: inApp ? "Merging happens on GitHub as you and cannot be undone." : undefined,
    // A round starts only from Review: `executing` with a pull request reads
    // as Review, and the server refuses the round there.
    secondary:
      facts.item.phase === "final_review" ? verb("request_changes", "Request changes") : null,
    primary: mergeVerb,
  };
}

/**
 * The footer when the pull request conflicts with its base and nobody else
 * decides first: Wright merging it in on its own, Fix the conflicts once it
 * no longer does, or the line alone for a person who took the ticket over.
 * Null when it does not conflict, or an ask a person owns says it.
 */
function conflictFooter(facts: SheetFooterFacts, label?: string): SheetFooterSpec | null {
  const conflict = facts.merge?.conflict;
  if (!conflict || conflict.fixer === "ask_open") return null;
  const viewPr = facts.item.prUrl ? verb("open_pr", viewPrLabel(facts.item.prNumber)) : null;
  if (canFixConflicts(conflict) && facts.item.phase === "final_review") {
    return {
      kind: label ? "generic" : "merge",
      ...(label ? { label } : {}),
      why: fixConflictsWhy(conflict),
      secondary: viewPr,
      primary: verb("fix_conflicts", FIX_CONFLICTS_VERB),
    };
  }
  const why = conflict.fixer === "wright" ? mergingBaseWhy(conflict) : prConflictLine(conflict);
  // The line alone for a person who took the ticket over: hand it back, and
  // Wright merges the base in once it is the Team's again.
  if (heldByAPerson(facts.item) && facts.item.phase === "final_review") {
    return {
      kind: label ? "generic" : "merge",
      ...(label ? { label } : {}),
      why: `${why} Handing it back gives it to the Team.`,
      secondary: viewPr,
      primary: verb("hand_back", HAND_BACK),
    };
  }
  return {
    kind: label ? "generic" : "merge",
    ...(label ? { label } : {}),
    why,
    secondary: null,
    primary: viewPr,
  };
}

/** Why a draft has no merge verb yet. */
function draftWhy(merge: MergeFacts): string {
  if (merge.heldReviewElsewhere) {
    return "Proctor approved this push. Someone who can open its review confirms it, and that takes it out of draft.";
  }
  if (merge.draftHeldUnverified) {
    return "This build did not pass everything, so it stays a draft until a fix round passes, or you take it out of draft on GitHub.";
  }
  return "Draft until checks pass and Proctor's review settles. It leaves draft by itself then.";
}

function workingWhy(facts: SheetFooterFacts): Pick<SheetFooterSpec, "why" | "bold"> {
  const stage = pipelineStageOf(facts.item);
  if (stage === "build")
    return {
      why: "A pull request opens when the build is done.",
      bold: "Proctor reviews it without you.",
    };
  if (stage === "qa")
    return { why: "Fletcher posts the evidence on the pull request when it finishes." };
  if (stage === "review")
    return { why: "The reviewer and the builder are working through this round." };
  if (stage === "plan")
    return {
      why: "Reeve asks you only if a question needs a person. Otherwise the plan moves on by itself: to your sign-off on a Supervised Team, straight to the build on an Autonomous one.",
    };
  return { why: "An agent is working on this now." };
}

const START_OVER_WHY =
  "Starting over throws this work away, and Reeve plans the ticket again from the start.";

/**
 * A one-attempt lane attempt that stopped and opened no ask: its pull request
 * closed on GitHub, a person stopped it, or its start failed. Nothing moves it
 * on its own, so the footer says it stopped and why, in the row's words, and
 * never the merge path's draft or merge sentence over a pull request nothing
 * will review or take out of draft (COREX-1291, prod 2026-10-27: "Draft until
 * checks pass…" under a closed pull request, with no way on). The verbs are
 * the rules' (`workItemVerbs.ts`): Run again where its run has something to
 * start from, Hand back for a person holding an open pull request, else Start
 * over, with Keep for a human beside it. Null for anything else, and for a
 * stop the plan's own footer answers (Retry plan, a refused start).
 *
 * The run says it is the lane's (`lane`); the item's stop code says so too
 * when its pull request closed, so a runs read served without the lane's
 * overlay never puts the draft footer back over a closed pull request.
 */
function stoppedAttemptFooter(
  facts: SheetFooterFacts,
  current: SheetFooterFacts["runs"][number] | null,
): SheetFooterSpec | null {
  const { item, runs } = facts;
  const prClosed = item.latestRunStopReason === PR_CLOSED_UNMERGED_STOP_REASON;
  if (!current || !STOPPED_RUN_STATUSES.has(current.status)) return null;
  // A ticket moved back to Proposed reads as a proposal whatever its old
  // attempt did: Start plan, not that attempt's stop (YAG-1098).
  if (item.phase === "proposed") return null;
  if (current.lane !== "attempt" && !prClosed) return null;
  if (facts.canStartPlan || item.planStartRefusal) return null;
  const stopped = itemStoppedSentence(item);
  const keep = verb("keep_for_human", "Keep for a human");
  const removed = Boolean(item.removedRepoRef);
  if (canRunAgain(current)) {
    // A lane attempt resumes itself; a legacy build re-offers its plan.
    const again =
      current.lane !== "attempt"
        ? "Running again starts a fresh attempt on the same plan."
        : prClosed
          ? "Running again reopens the pull request and picks the work up on the same branch."
          : "Running again picks the work up where it stopped, on the same branch.";
    return {
      kind: "stopped",
      why: `${stopped} ${again} ${KEEP_FOR_HUMAN_WHY}`,
      secondary: keep,
      primary: verb("run_again", "Run again", removed),
    };
  }
  // The rules read the item with its stage, as the More menu hands it to them.
  const ruled = { ...item, stage: pipelineStageOf(item) };
  if (!prClosed && canHandBack({ item: ruled, openGate: null, runs })) {
    return {
      kind: "stopped",
      why: `${stopped} You hold this ticket. Handing it back gives it to the Team.`,
      secondary: keep,
      primary: verb("hand_back", HAND_BACK, removed),
    };
  }
  if (canStartOver({ item: ruled, viewerIsTeamMember: facts.viewerIsTeamMember })) {
    return {
      kind: "stopped",
      why: `${stopped} ${START_OVER_WHY} ${KEEP_FOR_HUMAN_WHY}`,
      secondary: keep,
      primary: verb("start_over", START_OVER_MENU),
    };
  }
  return {
    kind: "stopped",
    why: `${stopped} Nothing here can restart this attempt on its own. YAGNI can say what it needs. ${KEEP_FOR_HUMAN_WHY}`,
    secondary: keep,
    primary: verb("ask_yagni", "Ask YAGNI"),
  };
}

/** The neutral footer while the ticket's runs are read: no verb, no claim. */
const RUNS_LOADING: SheetFooterSpec = {
  kind: "loading",
  why: "Reading what this ticket is doing.",
  secondary: null,
  primary: null,
};

/**
 * A tracker ticket on its way to Reeve (`workCopy.ts`): Bailey's check is
 * never a wait to skip. No Start plan while she reads it, Scope now while it
 * is queued behind the Team's readings, and a plan without her check only
 * behind a spent daily cap. The server holds the same line (`trackerSpec.ts`
 * `trackerStartPlan`).
 */
function trackerSpecFooter(facts: SheetFooterFacts, wait: TrackerSpecWait): SheetFooterSpec {
  const keep = verb("keep_proposal", "Keep for a human");
  if (wait === "queued") {
    return {
      kind: "proposed",
      why: TRACKER_SPEC_QUEUED_WHY,
      bold: TRACKER_SPEC_QUEUED_BOLD,
      secondary: keep,
      primary: verb("scope_now", SCOPE_NOW_VERB),
    };
  }
  if (wait === "daily_cap") {
    const capped = {
      kind: "proposed" as const,
      bold: trackerSpecDailyCapBold(facts.planAuthority),
      secondary: keep,
      primary: verb("start_plan", PLAN_WITHOUT_CHECK_VERB),
    };
    return facts.canEditWorkspaceSettings
      ? {
          ...capped,
          why: TRACKER_SPEC_DAILY_CAP_WHY,
          link: { lead: "Raise the limit in", ...DAILY_READINGS_SETTINGS },
        }
      : { ...capped, why: `${TRACKER_SPEC_DAILY_CAP_WHY} ${TRACKER_SPEC_DAILY_CAP_ASK_OWNER}` };
  }
  const key = facts.item.sourceRef?.identifier;
  return {
    kind: "proposed",
    why: trackerSpecReadingWhy(typeof key === "string" && key.trim() ? key.trim() : null),
    bold: planSignOffBold(facts.planAuthority),
    secondary: keep,
    primary: null,
  };
}

/** The footer for one ticket, from what is true of it right now. */
export function sheetFooterOf(facts: SheetFooterFacts): SheetFooterSpec {
  const { item, gate, runs } = facts;
  if (isTerminal(item) && !gate) return doneFooter(facts);
  if (item.archivedAt) {
    return {
      kind: "generic",
      label: "Archived",
      why: "It is out of every list until someone brings it back.",
      secondary: null,
      primary: verb("next_item", facts.hasNextItem ? "Next item" : "Back to the queue"),
    };
  }
  if (gate) return gateFooter(facts, gate);
  if (facts.gateError && item.currentGateId) {
    return {
      kind: "generic",
      label: "Not read",
      why: "Could not read what this ticket is waiting on.",
      whyInline: true,
      secondary: null,
      primary: verb("retry_gate", "Retry"),
    };
  }
  if (facts.gateLoading && item.currentGateId) {
    return {
      kind: "loading",
      why: "Reading what this ticket waits on.",
      secondary: null,
      primary: null,
    };
  }

  const current = runs[0] ?? null;
  const live = runs.some(isLiveRun) || item.activeRun != null;
  if (live) {
    const stoppable = runs.find(isLiveRun) ?? (current && canStopRun(current) ? current : null);
    return {
      kind: "working",
      ...workingWhy(facts),
      // The note is read where a build step or fix round starts; a run on a
      // proposal (Bailey's reading) has none coming. The chat's rule is the
      // same (`availableItemVerbs`, leave_next_step_note).
      ...(item.phase === "executing" || (runs.some(isLiveRun) && item.phase !== "proposed")
        ? { ask: verb("add_note", ADD_NOTE_LABEL) }
        : {}),
      secondary: stoppable ? verb("stop_run", "Stop") : null,
      primary: facts.wide ? null : verb("watch_live", "Watch live"),
    };
  }

  // The ticket names a run and the runs are not read yet: what it is doing is
  // unknown, not "Waiting its turn" (staging, 2026-10-21, a running build
  // opened on that footer and no live card until its runs landed).
  if (facts.runsLoading && runs.length === 0 && item.currentRunId != null) return RUNS_LOADING;

  const stoppedAttempt = stoppedAttemptFooter(facts, current);
  if (stoppedAttempt) return stoppedAttempt;

  const inReviewWithPr =
    item.kind !== "review" && Boolean(item.prUrl) && pipelineStageOf(item) === "review";
  if (inReviewWithPr) {
    if (!facts.merge)
      return { kind: "loading", why: "Reading the pull request.", secondary: null, primary: null };
    return mergeFooter(facts, facts.merge);
  }

  const trackerWait = item.phase === "proposed" ? trackerSpecWaitOf(item.sourceRef) : null;
  if (trackerWait) return trackerSpecFooter(facts, trackerWait);

  if (item.phase === "proposed") {
    return {
      kind: "proposed",
      why: "Starting the plan hands this to Reeve.",
      bold: planSignOffBold(facts.planAuthority),
      secondary: verb("keep_proposal", "Keep for a human"),
      primary: verb("start_plan", "Start plan"),
    };
  }

  if (item.isActivation) {
    return {
      kind: "generic",
      label: "Setup",
      why: "This advances on its own as each setup step lands. The next one is adding a Team.",
      secondary: null,
      primary: verb("ask_yagni", "Ask YAGNI"),
    };
  }

  // A person holds a ticket that never built: Reeve will not plan it until
  // it is the Team's again, and Start plan is not offered on a held ticket.
  if (isHeldBeforeBuild(item, runs)) {
    return {
      kind: "generic",
      label: "With you",
      why: "You hold this ticket, so Reeve does not plan it. Handing it to Reeve gives it back to the Team and starts the plan.",
      bold: planSignOffBold(facts.planAuthority),
      secondary: verb("keep_for_human", "Keep for a human"),
      primary: verb("hand_back", HAND_TO_REEVE, Boolean(item.removedRepoRef)),
    };
  }

  const refused = standingRefusalFooter(facts);
  if (refused) return refused;

  if (facts.canStartPlan) {
    const retry = current?.purpose === "planning";
    return {
      kind: "generic",
      label: "Plan",
      why: startPlanWhy(retry, facts.planAuthority),
      secondary: verb("keep_for_human", "Keep for a human"),
      primary: verb("start_planning", retry ? "Retry plan" : "Start plan"),
    };
  }

  if (current && canRunAgain(current) && heldByAPerson(item) && current.purpose === "execution") {
    return {
      kind: "stopped_infra",
      why: "You hold this ticket. Handing it back gives it to the Team and offers the approved plan again.",
      bold: "Nothing builds until the plan is approved.",
      secondary: verb("keep_for_human", "Keep for a human"),
      primary: verb("hand_back", HAND_BACK, Boolean(item.removedRepoRef)),
    };
  }

  if (current && canRunAgain(current)) {
    return {
      kind: "stopped_infra",
      why: `Running again starts a fresh attempt on the same plan. ${KEEP_FOR_HUMAN_WHY}`,
      bold: "The kept branch is reused.",
      secondary: verb("keep_for_human", "Keep for a human"),
      primary: verb("run_again", "Run again", Boolean(item.removedRepoRef)),
    };
  }

  const stopped = current != null && ["failed", "partial", "dismissed"].includes(current.status);
  if (stopped) {
    return {
      kind: "generic",
      label: "Stopped",
      why: `Nothing here can restart this attempt on its own. YAGNI can say what it needs. ${KEEP_FOR_HUMAN_WHY}`,
      secondary: verb("keep_for_human", "Keep for a human"),
      primary: verb("ask_yagni", "Ask YAGNI"),
    };
  }

  // Nothing above is known to be true until the runs are read.
  if (facts.runsLoading && runs.length === 0) return RUNS_LOADING;

  return {
    kind: "generic",
    label: "Waiting its turn",
    why: "Nothing waits on you here. It starts on its own when the Team has room.",
    secondary: null,
    primary: facts.hasNextItem ? verb("next_item", "Next item") : verb("ask_yagni", "Ask YAGNI"),
  };
}
